/**
 * Roadmap #146 Alt-PR C — fetch live preview metadata (PR title, branch,
 * spawn time, status). Backend endpoint lands in Alt-PR D; until then this
 * hook returns `null` for every preview and the overlay degrades to the
 * hostname-derived context.
 */
import { useEffect, useState } from 'react';
import { api } from '../../services/api.js';

export interface PreviewLink {
  roadmapItemId: number | null;
  aiIssueId: number | null;
  roadmapItemTitle: string | null;
  prUrl: string | null;
}

export interface PreviewMeta {
  name: string;
  branch: string;
  prNumber: number | null;
  prTitle: string | null;
  prAuthor: string | null;
  spawnedAt: string;
  hostname: string;
  // null = pending (not yet reviewed) — distinguished from `false` (failed).
  acceptance: { id: string; label: string; passed: boolean | null }[];
  state: 'spawning' | 'ready' | 'qa_pending' | 'qa_passed' | 'qa_rejected' | 'torndown' | 'failed';
  qaDecision: 'approved' | 'rejected' | null;
  // ISO string when the env was torn down. NULL means still live. Use
  // this — not `state` — to test "is the preview env actually running":
  // an env approved-then-torndown keeps state=qa_passed for audit but
  // gets torndownAt stamped.
  torndownAt: string | null;
  // Issue #142 — PR description (markdown). Cached server-side, only set
  // when the env is tied to a PR and the GitHub fetch succeeded.
  prBody?: string | null;
  link?: PreviewLink;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export function usePreviewMeta(name: string | null): {
  meta: PreviewMeta | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  setMeta: (meta: PreviewMeta | null) => void;
} {
  const [meta, setMeta] = useState<PreviewMeta | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!name) {
      setMeta(null);
      setLoading(false);
      return;
    }
    // Sentinel from previewContext when ?previewOverlay=on is set without
    // a real preview behind it — skip the request entirely so we don't
    // spam the API/console with a guaranteed 404.
    if (name === 'pr-DEV') {
      setMeta(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await api.get<ApiEnvelope<PreviewMeta>>('/preview/context', {
          params: { name },
        });
        if (cancelled) return;
        if (res.data.success && res.data.data) {
          setMeta(res.data.data);
        } else {
          setMeta(null);
          setError(res.data.error ?? 'preview context not available');
        }
      } catch (err) {
        if (cancelled) return;
        // 404 expected when env hasn't synced yet.
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status !== 404) {
          setError((err as Error).message);
        }
        setMeta(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [name, refreshTick]);

  return {
    meta,
    loading,
    error,
    refresh: () => setRefreshTick((t) => t + 1),
    setMeta,
  };
}
